/** Controles das preferências do loop (período, passo, velocidade, modo, pausa no fim). */
import clsx from 'clsx';
import { CalendarClock, Clock, Footprints, Hourglass, Info, SkipBack, SkipForward } from 'lucide-react';
import { useEffect, useId, useState } from 'react';
import { formatDateTimeBrt, fromBrtInputValue, HOUR_MS, MINUTE_MS, toBrtInputValue } from '../lib/time';
import { HISTORY_LIMIT_NOTE, MAX_HISTORY_HOURS, PERIOD_PRESETS, STEP_OPTIONS, type Period, type StepMinutes } from '../loop/frames';
import { formatBytes, formatInt } from '../lib/format';
import { usePlanStore } from '../loop/plan';
import { useDetalheStore } from '../detalhe/estado';
import { goToFirst, goToLast } from '../loop/playback';
import { DWELL_OPTIONS_MS, FPS_MAX, FPS_MIN, loopLoadAllowed, useLoopStore, type LoopMode, type LoopSettings } from '../loop/store';
import { useTimeStore } from '../state/time';
import { useUiStore } from '../state/ui';
import { useAvailableSpan, useLatestTime } from './hooks';
import { Button, Dialog, IconButton, InlineSelect, Segmented, Select, Slider, type SelectOption } from './primitives';
import { ResolutionSelect } from './ResolutionChip';

const PERIOD_LABELS: Record<Period, string> = {
  '1h': 'Última 1 h',
  '3h': 'Últimas 3 h',
  '6h': 'Últimas 6 h',
  '12h': 'Últimas 12 h',
  '24h': 'Últimas 24 h',
  '48h': 'Últimas 48 h',
  custom: 'Personalizado…',
};

export const PERIOD_OPTIONS: SelectOption<Period>[] = [...PERIOD_PRESETS, 'custom' as const].map((p) => ({
  value: p,
  label: PERIOD_LABELS[p],
}));

const STEP_LABELS: Record<StepMinutes, string> = { 10: 'a cada 10 min', 20: 'a cada 20 min', 30: 'a cada 30 min', 60: 'a cada 1 h' };
/** Rótulo curto do passo, para o resumo na barra de tempo. */
export const STEP_LABELS_SHORT: Record<StepMinutes, string> = { 10: '10 min', 20: '20 min', 30: '30 min', 60: '1 h' };
const STEP_OPTIONS_UI: SelectOption<string>[] = STEP_OPTIONS.map((s) => ({ value: String(s), label: STEP_LABELS[s] }));

const MODE_OPTIONS: SelectOption<LoopMode>[] = [
  { value: 'loop', label: 'Repetir' },
  { value: 'bounce', label: 'Vai e volta' },
];

const DWELL_OPTIONS: SelectOption<string>[] = DWELL_OPTIONS_MS.map((ms) => ({
  value: String(ms),
  label: ms === 0 ? 'Sem pausa no fim' : `Pausa no fim: ${String(ms / 1000).replace('.', ',')} s`,
}));

const setSettings = (patch: Partial<LoopSettings>) => useLoopStore.getState().setSettings(patch);

/** Troca o período; ao escolher "Personalizado" pela 1ª vez, parte da janela atual. */
function selectPeriod(period: Period, latest: number | null): void {
  if (period === 'custom' && !useLoopStore.getState().custom) {
    const range = useTimeStore.getState().range;
    const end = range?.end ?? latest ?? Date.now();
    setSettings({ period, custom: { start: range?.start ?? end - 3 * HOUR_MS, end } });
  } else {
    setSettings({ period });
  }
}

export function PeriodSelect(props: { className?: string }) {
  const period = useLoopStore((s) => s.period);
  const latest = useLatestTime();
  return (
    <InlineSelect
      label="Período do loop"
      icon={Clock}
      value={period}
      options={PERIOD_OPTIONS}
      onChange={(p) => {
        selectPeriod(p, latest);
        // O editor abre num diálogo: a barra de tempo nunca muda de altura.
        if (p === 'custom') useUiStore.getState().set({ customPeriodOpen: true });
      }}
      className={props.className}
    />
  );
}

export function StepSelect(props: { className?: string }) {
  const step = useLoopStore((s) => s.step);
  return (
    <InlineSelect
      label="Intervalo entre quadros"
      icon={Footprints}
      value={String(step)}
      options={STEP_OPTIONS_UI}
      onChange={(v) => setSettings({ step: Number(v) as StepMinutes })}
      className={props.className}
    />
  );
}

export function DwellSelect(props: { className?: string }) {
  const dwellMs = useLoopStore((s) => s.dwellMs);
  const options = DWELL_OPTIONS.some((o) => o.value === String(dwellMs))
    ? DWELL_OPTIONS
    : [...DWELL_OPTIONS, { value: String(dwellMs), label: `Pausa no fim: ${dwellMs / 1000} s` }];
  return (
    <InlineSelect
      label="Pausa extra no último quadro"
      icon={Hourglass}
      value={String(dwellMs)}
      options={options}
      onChange={(v) => setSettings({ dwellMs: Number(v) })}
      className={props.className}
    />
  );
}

export function ModeSegmented() {
  const mode = useLoopStore((s) => s.mode);
  return <Segmented value={mode} options={MODE_OPTIONS} onChange={(m) => setSettings({ mode: m })} size="sm" />;
}

/** Velocidade compacta (desktop). */
export function SpeedInline() {
  const fps = useLoopStore((s) => s.fps);
  const id = useId();
  return (
    <div className="flex items-center gap-2">
      <label htmlFor={id} className="text-xs text-ink-300">
        Velocidade
      </label>
      <input
        id={id}
        type="range"
        min={FPS_MIN}
        max={FPS_MAX}
        step={1}
        value={fps}
        onChange={(e) => setSettings({ fps: Number(e.target.value) })}
        className="w-24"
        aria-valuetext={`${fps} quadros por segundo`}
      />
      <span className="w-12 font-mono text-[11px] text-ink-200 tabular-nums">{fps} q/s</span>
    </div>
  );
}

/**
 * Valida o período personalizado contra o que a interface oferece — as últimas
 * 48 h (`useAvailableSpan`). Retorna a mensagem de erro, ou null se estiver ok.
 *
 * Datas anteriores não são buscadas: a NOAA ainda as guarda, mas as outras
 * camadas não, e o histórico delas depende de um servidor que ainda não existe.
 */
export function validateCustomPeriod(
  start: number | null,
  end: number | null,
  span: [number, number] | null,
): string | null {
  if (start === null || end === null) return 'Preencha o início e o fim.';
  if (start >= end) return 'O início precisa ser antes do fim.';
  if (span) {
    // Os campos só aceitam minutos inteiros: arredonda o início para fora.
    const first = Math.floor(span[0] / MINUTE_MS) * MINUTE_MS;
    if (start < first) {
      return `Por enquanto, só as últimas ${MAX_HISTORY_HOURS} h: escolha a partir de ${formatDateTimeBrt(span[0])} (Brasília). ${HISTORY_LIMIT_NOTE}`;
    }
    if (end > span[1] + MINUTE_MS - 1) {
      return `O fim vai até ${formatDateTimeBrt(span[1])} (Brasília), a imagem mais recente.`;
    }
  }
  return null;
}

/** Início/fim do período personalizado (horário de Brasília). */
export function CustomPeriodEditor(props: { onApplied?: () => void }) {
  const custom = useLoopStore((s) => s.custom);
  const span = useAvailableSpan();
  const [start, setStart] = useState(custom ? toBrtInputValue(custom.start) : '');
  const [end, setEnd] = useState(custom ? toBrtInputValue(custom.end) : '');
  const startId = useId();
  const endId = useId();

  useEffect(() => {
    if (!custom) return;
    setStart(toBrtInputValue(custom.start));
    setEnd(toBrtInputValue(custom.end));
  }, [custom]);

  const s = fromBrtInputValue(start);
  const e = fromBrtInputValue(end);
  const message = validateCustomPeriod(s, e, span);
  const invalid = message !== null;
  const changed = !custom || s !== custom.start || e !== custom.end;
  // Os campos só aceitam minutos inteiros: arredonda os limites para fora.
  const min = span ? toBrtInputValue(Math.floor(span[0] / MINUTE_MS) * MINUTE_MS) : undefined;
  const max = span ? toBrtInputValue(span[1]) : undefined;
  const inputClass =
    'h-8 rounded-lg border border-ink-600 bg-ink-800 px-2 text-xs text-ink-100 [color-scheme:dark] focus:border-mata-400 focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-mata-400 aria-[invalid=true]:border-yellow-400';

  return (
    <div className="flex flex-wrap items-end gap-2">
      <div className="flex flex-col gap-1">
        <label htmlFor={startId} className="text-[11px] text-ink-300">
          Início (Brasília)
        </label>
        <input
          id={startId}
          type="datetime-local"
          className={inputClass}
          value={start}
          min={min}
          max={max}
          aria-invalid={invalid && !!start}
          aria-describedby={`${startId}-msg`}
          onChange={(ev) => setStart(ev.target.value)}
        />
      </div>
      <div className="flex flex-col gap-1">
        <label htmlFor={endId} className="text-[11px] text-ink-300">
          Fim (Brasília)
        </label>
        <input
          id={endId}
          type="datetime-local"
          className={inputClass}
          value={end}
          min={min}
          max={max}
          aria-invalid={invalid && !!end}
          aria-describedby={`${startId}-msg`}
          onChange={(ev) => setEnd(ev.target.value)}
        />
      </div>
      <Button
        size="sm"
        variant="primary"
        icon={CalendarClock}
        disabled={invalid || !changed}
        onClick={() => {
          if (invalid) return;
          setSettings({ custom: { start: s!, end: e! } });
          props.onApplied?.();
        }}
      >
        Aplicar
      </Button>
      <p id={`${startId}-msg`} className={clsx('w-full text-[11px]', invalid && start && end ? 'text-yellow-300' : 'text-ink-300')} role="status">
        {invalid && start && end
          ? message
          : span
            ? `Disponível de ${formatDateTimeBrt(span[0])} a ${formatDateTimeBrt(span[1])} (Brasília) — as últimas ${MAX_HISTORY_HOURS} h.`
            : 'Buscando o que está disponível…'}
      </p>
      {/* O aviso fica sempre à vista, e não só depois do erro: quem procura um
          dia da semana passada precisa saber antes de tentar. */}
      {!(invalid && start && end) && (
        <p className="flex w-full items-start gap-1.5 text-[11px] leading-snug text-ink-400">
          <Info size={12} className="mt-px shrink-0" aria-hidden />
          {HISTORY_LIMIT_NOTE}
        </p>
      )}
    </div>
  );
}

/**
 * Botão "editar período" ao lado do seletor. O espaço fica sempre reservado
 * (invisível fora do período personalizado) para a barra não mudar de largura.
 */
export function CustomPeriodButton() {
  const custom = useLoopStore((s) => s.period === 'custom');
  return (
    <span className={clsx('inline-flex', !custom && 'invisible')} aria-hidden={!custom}>
      <IconButton
        icon={CalendarClock}
        size="sm"
        label="Editar o período personalizado"
        disabled={!custom}
        onClick={() => useUiStore.getState().set({ customPeriodOpen: true })}
        className="pointer-coarse:size-11"
      />
    </span>
  );
}

/** Editor do período personalizado num diálogo (fora da barra de tempo). */
export function CustomPeriodDialog() {
  const open = useUiStore((s) => s.customPeriodOpen);
  const close = () => useUiStore.getState().set({ customPeriodOpen: false });
  return (
    <Dialog open={open} onClose={close} title="Período personalizado">
      <CustomPeriodEditor onApplied={close} />
    </Dialog>
  );
}

/** Situação do download do loop (dentro do diálogo de opções). */
function DownloadStatus() {
  const { downloaded, failed, total, bytesDownloaded, bytesTotal } = usePlanStore((s) => s.progress);
  const nivel = useDetalheStore((s) => s.nivel);
  const blocos = useDetalheStore((s) => s.progresso);
  const loopAll = useLoopStore((s) => loopLoadAllowed(s));
  if (total === 0) return null;
  const done = downloaded + failed >= total;
  return (
    <p className="text-xs text-ink-300 tabular-nums" role="status">
      {done
        ? `${formatInt(downloaded)} imagens baixadas · ${formatBytes(bytesDownloaded)}`
        : !loopAll
          ? // O portal abre parado na medição mais recente: o resto baixa ao tocar.
            `Mostrando a imagem mais recente. As ${formatInt(total)} imagens do período (≈ ${formatBytes(bytesTotal)}) baixam quando você toca a animação.`
          : `Baixando ${formatInt(downloaded)} de ${formatInt(total)} · ${formatBytes(bytesDownloaded)} de ${formatBytes(bytesTotal)}. A animação já pode tocar enquanto baixa.`}
      {failed > 0 && <span className="text-yellow-300"> · {failed === 1 ? '1 falhou' : `${formatInt(failed)} falharam`}</span>}
      {nivel && <> · detalhe {nivel} px em blocos: {formatBytes(blocos.bytes)}</>}
    </p>
  );
}

/**
 * Opções do loop num diálogo. No desktop o período fica na barra e aqui só
 * repete; no celular (`mobile`) o diálogo também traz início/fim do loop.
 */
export function LoopSettingsDialog(props: { open: boolean; onClose: () => void; mobile?: boolean }) {
  const period = useLoopStore((s) => s.period);
  const step = useLoopStore((s) => s.step);
  const fps = useLoopStore((s) => s.fps);
  const mode = useLoopStore((s) => s.mode);
  const dwellMs = useLoopStore((s) => s.dwellMs);
  const latest = useLatestTime();
  return (
    <Dialog open={props.open} onClose={props.onClose} title="Opções da animação">
      <div className="space-y-4">
        {props.mobile && (
          <div className="flex gap-2">
            <Button size="sm" icon={SkipBack} onClick={goToFirst} className="min-h-11 flex-1">
              Primeiro quadro
            </Button>
            <Button size="sm" icon={SkipForward} onClick={goToLast} className="min-h-11 flex-1">
              Último quadro
            </Button>
          </div>
        )}
        <Select label="Período" value={period} options={PERIOD_OPTIONS} onChange={(p) => selectPeriod(p, latest)} />
        {period === 'custom' && <CustomPeriodEditor />}
        <Select
          label="Intervalo entre quadros"
          value={String(step)}
          options={STEP_OPTIONS_UI}
          onChange={(v) => setSettings({ step: Number(v) as StepMinutes })}
        />
        <Slider label="Velocidade" value={fps} min={FPS_MIN} max={FPS_MAX} format={(v) => `${v} quadros/s`} onChange={(v) => setSettings({ fps: v })} />
        <Segmented label="Ao chegar ao fim" value={mode} options={MODE_OPTIONS} onChange={(m) => setSettings({ mode: m })} />
        <Select label="Pausa no último quadro" value={String(dwellMs)} options={DWELL_OPTIONS} onChange={(v) => setSettings({ dwellMs: Number(v) })} />
        {props.mobile && <ResolutionSelect />}
        <DownloadStatus />
        {props.mobile && <p className="text-xs text-ink-400">Horários em Brasília (UTC−3). No desktop o relógio também mostra o UTC.</p>}
      </div>
    </Dialog>
  );
}
